import { Flex, Grid } from "styled-system/jsx";
import { Button, Menu, SimpleDialog } from "@ttc3k/trekker";
import { FloppyDisk, MoreHoriz } from "iconoir-react";
import { useTranslation } from "react-i18next";
import {
  EnumEventAccessOperatorStatus,
  EnumEventLocationType,
  EnumOwnerType,
} from "gql/generated";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";
import { useCallback, useMemo, useState } from "react";
import { ProgressStepper } from "shared/components/molecules/ProgressStepper/ProgressStepper";

type EventCreateEditModalFooterProps = {
  isSaving: boolean;
  currentStep: number;
  totalSteps: number;
  userType: EnumOwnerType | "PUBLIC";
  hasDirtyFields: boolean;
  formTriggerFn: () => Promise<boolean>;
  handleApproveClick: () => void;
  handleDeclineClick: () => void;
  handleEditClick: () => void;
  handleMenuAction: (item: { value: "cancel" | "uncancel" | "decline" | "delete" | "disconnect" }) => void;
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  handleJumpToStepClick: (step: number) => void;
  handleSubmitClick: () => void;
  handleSaveClick: () => void;
  isVirtual: boolean;
  hideMenuButtons?: boolean;
};

export const EventCreateEditModalFooter = ({
  isSaving,
  currentStep,
  totalSteps,
  userType,
  hasDirtyFields,
  formTriggerFn,
  handleApproveClick,
  handleDeclineClick,
  handleEditClick,
  handleMenuAction,
  handleNextClick,
  handlePreviousClick,
  handleJumpToStepClick,
  handleSubmitClick,
  handleSaveClick,
  isVirtual,
  hideMenuButtons,
}: EventCreateEditModalFooterProps) => {
  const { t } = useTranslation("events", { keyPrefix: "APP.MODAL" });

  const {
    event,
    isOrganizationUser,
    isOrganizationAdmin,
    isTnbInternalUser,
    isMultiOrgEvent,
    mode,
    status,
    isOperatorUser,
    isOwner,
  } = useEventCreateEditContext();

  /** Operators should not "decline" their own submissions — use Cancel / Delete instead. */
  const hideDeclineForOperatorOwnEvent = isOperatorUser && isOwner;

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  /**
   * When clicking the next button, PUBLIC users need to be shown a confirm modal
   * letting them know that continuing will SAVE the event as a draft that will
   * be visible to the organization. And than the last step will allow them to set the
   * draft as ready for review.
   * - This confirmation modal should ONLY be shown if there is NO eventId
   */
  const handleNextForUserType = useCallback(async () => {
    if (userType !== "PUBLIC") {
      // Organization and Operator users can just click next
      handleNextClick();
    } else {
      if (currentStep === 1 && !event) {
        const success = await formTriggerFn();
        if (success) {
          setIsConfirmModalOpen(true);
        }
      } else {
        handleNextClick();
      }
    }
  }, [currentStep, event, userType, formTriggerFn, handleNextClick]);

  const handleConfirmModalConfirm = () => {
    setIsConfirmModalOpen(false);
    handleNextClick();
  };

  const progressSteps = useMemo(
    () => [
      {
        label: `1. ${t("EVENT_DETAILS.TITLE")}`,
        disabled: false,
      },
      {
        label: `2. ${t("EVENT_HOST.TITLE")} ${isVirtual ? t("EVENT_HOST.NOT_APPLICABLE") : ""}`,
        disabled:
          !event ||
          !event._id ||
          event?.locationType === EnumEventLocationType.Virtual ||
          isVirtual,
      },
      {
        label: `3. ${t("EVENT_PHOTOS.TITLE")}`,
        disabled: !event || !event._id,
      },
      {
        label: `4. ${t("EVENT_SCHEDULE.TITLE")}`,
        disabled: !event || !event._id,
      },
      {
        label: `5. ${t("EVENT_PREVIEW.TITLE")}`,
        disabled: !event || !event._id,
      },
    ],
    [event, isVirtual, t],
  );

  return (
    <>
      <Grid
        gridTemplateColumns={mode === "approve" ? "1fr 1fr" : "1fr 1fr 1fr"}
        gridColumnGap="150"
        py="300"
        px={{
          base: "150",
          sm: "300",
          md: "500",
        }}
        borderTop="1px solid"
        borderColor="border.light"
        boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.10)"
      >
        <Flex justifyContent="flex-start" alignItems="center" gap="150">
          {/* The cancel/delete action buttons */}
          {userType === "PUBLIC" || hideMenuButtons ? null : mode ===
              "approve" && !hideDeclineForOperatorOwnEvent ? (
              <Button
                visual="primary"
                mode="light"
                disabled={isSaving}
                onClick={handleDeclineClick}
              >
                {t("FOOTER.DECLINE")}...
              </Button>
            ) : (
              <Menu.Root
                trigger={
                  <Button
                    visual="primary"
                    mode="light"
                    w="fit-content"
                    Icon={MoreHoriz}
                    disabled={isSaving}
                  />
                }
                positioning={{
                  placement: "top",
                  offset: {
                    crossAxis: 20,
                  },
                }}
                onSelect={(item: { value: string }) =>
                  handleMenuAction(item as { value: "cancel" | "uncancel" | "decline" | "delete" | "disconnect" })
                }
              >
                {status !== EnumEventAccessOperatorStatus.Declined &&
                  !hideDeclineForOperatorOwnEvent && (
                    <Menu.Item value="decline">{t("FOOTER.DECLINE")}...</Menu.Item>
                  )}
                {status === EnumEventAccessOperatorStatus.Cancelled ? (
                  <Menu.Item value="uncancel">{t("FOOTER.UNCANCEL")}...</Menu.Item>
                ) : (
                  <Menu.Item value="cancel">{t("FOOTER.CANCEL")}...</Menu.Item>
                )}
                {(isOrganizationAdmin ||
                  isTnbInternalUser ||
                  (isOperatorUser &&
                    isOwner &&
                    status === EnumEventAccessOperatorStatus.Draft)) &&
                  (isMultiOrgEvent &&
                  !isTnbInternalUser &&
                  !(
                    isOperatorUser &&
                    status === EnumEventAccessOperatorStatus.Draft
                  ) ? (
                    <Menu.Item value="disconnect">
                      {t("FOOTER.DISCONNECT")}...
                    </Menu.Item>
                  ) : (
                    <Menu.Item value="delete">{t("FOOTER.DELETE")}...</Menu.Item>
                  ))}
              </Menu.Root>
            )}

          {/* Save button (all states except approve) - only this button shows spinner when saving */}
          {mode !== "approve" && (
            <Button
              visual="primary"
              mode="light"
              Icon={FloppyDisk}
              isLoading={isSaving}
              onClick={handleSaveClick}
              disabled={!hasDirtyFields}
            />
          )}
        </Flex>

        {/* The step indicator (only shown in create/edit mode) */}
        {mode !== "approve" && (
          <ProgressStepper
            currentStep={currentStep}
            steps={progressSteps}
            onJumpToStepClick={handleJumpToStepClick}
            onPreviousClick={handlePreviousClick}
            onNextClick={handleNextForUserType}
            isLoading={false}
            disabled={isSaving}
            menuProps={{
              positioning: {
                placement: "top",
                offset: {
                  crossAxis: -15,
                },
              },
            }}
          />
        )}

        {/* The save/next action buttons */}
        <Flex justifyContent="flex-end" alignItems="center" gap="150">
          {!hideMenuButtons && mode === "approve" && (
            <>
              <Button
                visual="primary"
                mode="light"
                disabled={isSaving}
                onClick={handleEditClick}
              >
                {t("FOOTER.EDIT")}
              </Button>
              {status !== EnumEventAccessOperatorStatus.Approved && (
                <Button
                  visual="success"
                  disabled={isSaving}
                  onClick={handleApproveClick}
                >
                  {t("FOOTER.APPROVE")}
                </Button>
              )}
            </>
          )}
          {!hideMenuButtons &&
            (mode === "edit" || currentStep === totalSteps) && (
              <Button
                visual="success"
                disabled={
                  isSaving ||
                  (isOrganizationUser &&
                    status === EnumEventAccessOperatorStatus.Approved)
                }
                onClick={handleSubmitClick}
              >
                {isOrganizationUser
                  ? t("FOOTER.APPROVE")
                  : status !== EnumEventAccessOperatorStatus.Draft
                    ? t("FOOTER.RESUBMIT")
                    : t("FOOTER.SUBMIT")}
              </Button>
            )}
        </Flex>
      </Grid>

      <SimpleDialog
        open={isConfirmModalOpen}
        title={t("FOOTER.CONFIRM_MODAL.TITLE")}
        message={t("FOOTER.CONFIRM_MODAL.DESCRIPTION")}
        cancelButtonCopy={t("FOOTER.CONFIRM_MODAL.CANCEL")}
        onCancelClick={() => setIsConfirmModalOpen(false)}
        confirmButtonCopy={t("FOOTER.CONFIRM_MODAL.CONFIRM")}
        confirmButtonVisual="destructive"
        onConfirmClick={handleConfirmModalConfirm}
        size="md"
        portal
      />
    </>
  );
};
